/**
 * Catálogo dos tipos de documento exigidos numa venda (P4 / P2.3). Ex.: Contrato,
 * Layout, Croqui, Descritivo. Tela admin: criar / ativar / ordenar / remover.
 * Gated por PROJETOS_V2_ENABLED.
 *
 * Vive embutido em Catálogos › Projetos. FAXINA 2026-07-31 (D-11): a rota direta
 * /admin/projeto-documento-tipos virou redirect e a prop `embedded` saiu.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

import {
  Button,
  Card,
  EmptyState,
  Input,
  Label,
  Select,
  SelectOption,
  Switch,
} from '@/components/ui';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import {
  useDocumentoTipos,
  useDocumentoTipoActions,
  type DocumentoTipo,
  type DocTipoVenda,
} from '@/lib/projetos-store';

// A2: rótulo do escopo do documento (a qual tipo de venda ele se aplica).
const TIPO_VENDA_LABEL: Record<string, string> = {
  '': 'Todas as vendas',
  projeto: 'Venda de projeto',
  recorrente: 'Venda recorrente',
};

export function ProjetoDocumentoTiposPage() {
  const projetosV2 = useMyFeatureFlag('PROJETOS_V2_ENABLED');
  const { data, isLoading } = useDocumentoTipos(false);
  const { createTipo, reorderTipos } = useDocumentoTipoActions();
  const [nome, setNome] = useState('');
  // A2: escopo do novo documento ('' = todas as vendas).
  const [novoEscopo, setNovoEscopo] = useState<'' | DocTipoVenda>('');

  // Embutida em Catálogos › Projetos: sem módulo, a sub-aba não renderiza nada.
  if (!projetosV2) {
    return null;
  }

  const items = data?.items ?? [];

  function adicionar() {
    if (!nome.trim()) return;
    createTipo.mutate(
      { nome: nome.trim(), tipoVenda: novoEscopo === '' ? null : novoEscopo },
      { onSuccess: () => setNome('') },
    );
  }

  function mover(idx: number, dir: -1 | 1) {
    const alvo = idx + dir;
    if (alvo < 0 || alvo >= items.length) return;
    const ids = items.map((t) => t.id);
    [ids[idx], ids[alvo]] = [ids[alvo], ids[idx]];
    reorderTipos.mutate(ids);
  }

  const legenda = (
    <>
      Tipos de anexo que o Comercial precisa juntar no card antes de virar projeto (ex.:
      Contrato, Layout, Croqui). O card mostra o que ainda falta e o projeto herda a lista.
      Cada tipo pode ser exigido <strong>por tipo de venda</strong> (projeto ou recorrente)
      ou em <strong>todas</strong>.
    </>
  );

  return (
    <div className="min-w-0">
      {/* FASE-C: h1 canônico só na rota própria — embutido em Catálogos a
          sub-aba já nomeia a tela (teste garante que não há h1). O padding de
          página saiu: o AdminLayout já aplica px-4 md:px-8. */}
      {/* LAYOUT-CANONICO: o `max-w-3xl` da rota própria saiu — a largura é a da
          casca (PageContainer no AdminLayout), igual à do modo embutido. */}
      <p className="mb-4 text-sm text-ink-3 dark:text-ink-dark-3">{legenda}</p>

      <Card className="min-w-0 p-3 mb-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px] max-w-full">
            <Label htmlFor="doc-nome">Novo tipo de documento</Label>
            <Input
              id="doc-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Contrato"
              data-testid="doc-tipo-nome"
            />
          </div>
          <div className="min-w-[160px] max-w-full">
            <Label htmlFor="doc-escopo">Exigido em</Label>
            <Select
              id="doc-escopo"
              value={novoEscopo}
              onChange={(e) => setNovoEscopo(e.target.value as '' | DocTipoVenda)}
              data-testid="doc-tipo-escopo"
            >
              <SelectOption value="">Todas as vendas</SelectOption>
              <SelectOption value="projeto">Venda de projeto</SelectOption>
              <SelectOption value="recorrente">Venda recorrente</SelectOption>
            </Select>
          </div>
          <Button onClick={adicionar} disabled={!nome.trim() || createTipo.isPending} data-testid="doc-tipo-add">
            Adicionar
          </Button>
        </div>
      </Card>

      {isLoading ? (
        <Card className="min-w-0 p-4 text-sm text-ink-3 dark:text-ink-dark-3 italic">
          Carregando…
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          title="Catálogo vazio"
          description="Adicione os tipos de documento que devem ser exigidos nas vendas de projeto."
        />
      ) : (
        <div className="space-y-2" data-testid="doc-tipos-list">
          {items.map((t, idx) => (
            <DocumentoTipoRow
              key={t.id}
              tipo={t}
              isFirst={idx === 0}
              isLast={idx === items.length - 1}
              onMoveUp={() => mover(idx, -1)}
              onMoveDown={() => mover(idx, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentoTipoRow({
  tipo,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
}: {
  tipo: DocumentoTipo;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const { updateTipo, deleteTipo } = useDocumentoTipoActions();
  const [editNome, setEditNome] = useState<string | null>(null);

  return (
    <Card
      className="min-w-0 p-3 flex flex-wrap items-center gap-2"
      data-testid={`doc-tipo-row-${tipo.id}`}
    >
      {/* FASE-C: os glifos ▴ ▾ viraram ícones Lucide. */}
      <div className="flex shrink-0 flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst}
          className="text-ink-3 dark:text-ink-dark-3 disabled:opacity-30 leading-none"
          aria-label={`Subir ${tipo.nome}`}
        >
          <ChevronUp className="w-4 h-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast}
          className="text-ink-3 dark:text-ink-dark-3 disabled:opacity-30 leading-none"
          aria-label={`Descer ${tipo.nome}`}
        >
          <ChevronDown className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      {editNome === null ? (
        <span
          className={`flex-1 min-w-0 break-words font-medium ${tipo.ativo ? 'text-ink-1 dark:text-ink-dark-1' : 'text-ink-3 dark:text-ink-dark-3 line-through'}`}
        >
          {tipo.nome}
        </span>
      ) : (
        <Input
          value={editNome}
          onChange={(e) => setEditNome(e.target.value)}
          className="flex-1 min-w-[160px] max-w-full"
          data-testid={`doc-tipo-edit-${tipo.id}`}
        />
      )}
      {editNome === null && (
        <Select
          value={tipo.tipoVenda ?? ''}
          onChange={(e) =>
            updateTipo.mutate({
              id: tipo.id,
              patch: { tipoVenda: e.target.value === '' ? null : (e.target.value as DocTipoVenda) },
            })
          }
          className="w-auto max-w-full text-xs"
          aria-label={`Em quais vendas ${tipo.nome} é exigido`}
          title={`Exigido em: ${TIPO_VENDA_LABEL[tipo.tipoVenda ?? '']}`}
          data-testid={`doc-tipo-escopo-${tipo.id}`}
        >
          <SelectOption value="">Todas</SelectOption>
          <SelectOption value="projeto">Projeto</SelectOption>
          <SelectOption value="recorrente">Recorrente</SelectOption>
        </Select>
      )}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Switch
          checked={tipo.ativo}
          onChange={(e) => updateTipo.mutate({ id: tipo.id, patch: { ativo: e.target.checked } })}
          aria-label={`${tipo.ativo ? 'Desativar' : 'Ativar'} ${tipo.nome}`}
          data-testid={`doc-tipo-ativo-${tipo.id}`}
        />
        {editNome === null ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditNome(tipo.nome)} data-testid={`doc-tipo-editar-${tipo.id}`}>
              Editar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteTipo.mutate(tipo.id)}
              aria-label={`Remover ${tipo.nome}`}
              data-testid={`doc-tipo-remover-${tipo.id}`}
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              onClick={() =>
                updateTipo.mutate(
                  { id: tipo.id, patch: { nome: (editNome ?? '').trim() } },
                  { onSuccess: () => setEditNome(null) },
                )
              }
              disabled={!(editNome ?? '').trim()}
            >
              Salvar
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditNome(null)}>
              Cancelar
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
